import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import './Cards.css';
import { config } from '../utils/config';
import MaintRecordModal from './MaintRecordModal';
import { getDeviceInfo as apiDeviceInfo } from '../services/device.js';
import { GreenIcon, YellowIcon, RedIcon, GrayIcon } from './StatusIcons';
import { buildWorstStatusMap } from '../utils/alertStatus';

/**
 * Static field definitions by appType
 */
const EQUIPMENT_FIELDS = {
    default: [
        { label: '●測量狀態', field: 'status' },
        { label: '●區間最大值', field: 'max_value' },
        { label: '●區間最小值', field: 'min_value' },
        { label: '●設備編碼', field: 'inid' },
        { label: '●設備型號', field: 'model' },
        { label: '●邊坡', field: 'slope_type' },
        { label: '●里程位置', field: 'position_mile' },
        { label: '●經度', field: 'lng' },
        { label: '●緯度', field: 'lat' },
        { label: '●備註', field: '-' },
    ],
    zoo: [
        { label: '●測量狀態', field: 'status' },
        { label: '●區間最大值', field: 'max_value' },
        { label: '●區間最小值', field: 'min_value' },
        { label: '●設備編碼', field: 'name' },
        { label: '●設備型號', field: 'model' },
        { label: '●備註', field: 'remarks' },
    ],
};

/**
 * Status presentation
 */
const STATUS_PRESENT = {
    green: { icon: <GreenIcon />, text: '正常' },
    yellow: { icon: <YellowIcon />, text: '超過警戒值' },
    red: { icon: <RedIcon />, text: '超過行動值' },
    gray: { icon: <GrayIcon />, text: '自動化超過12小時無資料回傳或尚無監測資料' },
};

const FALLBACK_STATUS = STATUS_PRESENT.gray;

const formatSecondsWithUnit = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return null;
    const trimmed = Number(num.toFixed(2)).toString();
    return `${trimmed}秒`;
};

function EquipmentStatusCard({
    styleColor,
    deviceId,
    alertData,
    maxValue,
    minValue,
    intervalExtremes,
}) {
    const [showHistory, setShowHistory] = useState(false);
    const [deviceInfo, setDeviceInfo] = useState(null); // not rendered yet, but kept for future use

    // Choose field template by appType once
    const equipmentStatusData = useMemo(
        () => EQUIPMENT_FIELDS[config?.appType] || EQUIPMENT_FIELDS.default,
        []
    );

    // Build a map for quick status lookups (stable memo)
    const alertIndex = useMemo(
        () =>
            buildWorstStatusMap(alertData, {
                getKey: (alert) => alert?.device_id,
            }),
        [alertData]
    );

    // Compute current measurement status from alertData + deviceId (no extra state)
    const measurementStatus = useMemo(() => {
        const statusKey = alertIndex.get(deviceId);
        return (
            STATUS_PRESENT[statusKey] ||
            (statusKey ? STATUS_PRESENT.green : FALLBACK_STATUS)
        );
    }, [alertIndex, deviceId]);

    // Fetch device info (abort-safe). Kept even if not yet used in render.
    useEffect(() => {
        if (!deviceId) return;
        const controller = new AbortController();
        (async () => {
            try {
                const data = await apiDeviceInfo(deviceId, { signal: controller.signal });
                setDeviceInfo(data);
            } catch (err) {
                if (err?.name !== 'AbortError') {
                    // eslint-disable-next-line no-console
                    console.error('Error fetching device info:', err);
                }
            }
        })();
        return () => controller.abort();
    }, [deviceId]);

    const tiIntervalDisplays = useMemo(() => {
        if (!Array.isArray(intervalExtremes) || intervalExtremes.length === 0) {
            return { max: null, min: null };
        }

        const buildDisplay = (key) => {
            const segments = intervalExtremes
                .map(({ label, [key]: value }) => {
                    const formatted = formatSecondsWithUnit(value);
                    if (!formatted) return null;
                    return `${label}：${formatted}`;
                })
                .filter(Boolean);
            return segments.length ? segments.join(' / ') : null;
        };

        return {
            max: buildDisplay('max'),
            min: buildDisplay('min'),
        };
    }, [intervalExtremes]);

    const renderDefaultValue = (field) => (
        <span className="value">{deviceInfo?.[field] ?? '-'}</span>
    );

    return (
        <div className="card equipment">
            <h3 className="card-title" style={{ color: styleColor }}>
                設備狀態
            </h3>

            <div className="card-content">
                {equipmentStatusData.map((it) => {
                    let value;

                    if (it.label === '●測量狀態') {
                        value = (
                            <span className="value">
                                {measurementStatus.icon}
                                {measurementStatus.text}
                            </span>
                        );
                    } else if (it.field === 'max_value') {
                        if (tiIntervalDisplays.max) {
                            value = (
                                <span className="value">{tiIntervalDisplays.max}</span>
                            );
                        } else if (maxValue !== undefined && Number.isFinite(maxValue)) {
                            value = <span className="value">{maxValue}</span>;
                        } else {
                            value = renderDefaultValue(it.field);
                        }
                    } else if (it.field === 'min_value') {
                        if (tiIntervalDisplays.min) {
                            value = (
                                <span className="value">{tiIntervalDisplays.min}</span>
                            );
                        } else if (minValue !== undefined && Number.isFinite(minValue)) {
                            value = <span className="value">{minValue}</span>;
                        } else {
                            value = renderDefaultValue(it.field);
                        }
                    } else {
                        value = renderDefaultValue(it.field);
                    }

                    return (
                        <React.Fragment key={it.label}>
                            <span className="label">{it.label}</span>
                            <div className="value-container">{value}</div>
                        </React.Fragment>
                    );
                })}
            </div>

            <button
                className="history-btn"
                style={{ color: 'white', backgroundColor: styleColor || '#000' }}
                onClick={() => setShowHistory(true)}
            >
                歷程管理
            </button>

            {showHistory && (
                <MaintRecordModal
                    deviceId={deviceId}
                    onClose={() => setShowHistory(false)}
                    styleColor={styleColor}
                />
            )}
        </div>
    );
}

EquipmentStatusCard.propTypes = {
    styleColor: PropTypes.string,
    deviceId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    alertData: PropTypes.arrayOf(
        PropTypes.shape({
            device_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
            status: PropTypes.oneOf(['green', 'yellow', 'red', 'gray']),
        })
    ),
    maxValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    minValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    intervalExtremes: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string.isRequired,
            max: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
            min: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        })
    ),
};

EquipmentStatusCard.defaultProps = {
    styleColor: '#000',
    alertData: [],
    maxValue: undefined,
    minValue: undefined,
    intervalExtremes: [],
};

export default React.memo(EquipmentStatusCard);
